"use client";

import { useEffect, useState } from "react";
import { useLiveSession } from "@/contexts/live-session-context";
import { useStudio } from "@/contexts/studio-context";
import { outputLatencySeconds } from "@/lib/app/audio-timebase";
import { playoutOverrunCount } from "@/lib/live/note-trace";
import { positionNowMs } from "@/lib/live/session-state";

/**
 * Les grandeurs de l'alignement, lues à l'écran.
 *
 * # Pourquoi ce panneau existe
 *
 * Quand deux transports divergent, aucun journal local ne dit de combien :
 * l'écart n'existe qu'entre les deux machines. On peut passer des heures à
 * former des hypothèses — c'est arrivé — ou afficher les quatre nombres et
 * comparer deux écrans.
 *
 * Ce qu'il montre est choisi pour **localiser** la coupure, pas pour informer :
 *
 * - `ma position` et `position visée` divergent → le calcul de l'origine est en
 *   cause, ou quelque chose la réécrit après nous.
 * - les deux concordent mais diffèrent entre les machines → c'est l'état publié
 *   ou l'écart d'horloge, pas l'application.
 * - `révision` diffère → une machine n'a pas reçu la dernière métadonnée.
 * - `âge` grandit sans fin → personne ne republie, ou la séance est figée.
 * - `mon délai` et `délai de restitution` diffèrent → mon délai n'a pas encore
 *   atteint le transport, ou quelque chose l'écrit en dehors du contexte. Il
 *   n'y a plus d'interrupteur local qui puisse les séparer : le délai est
 *   celui que le service me donne, et rien d'autre. Seul écart attendu : chez
 *   un spectateur de récital, le transport tient en plus son horizon (voir
 *   `transportDelayMs`).
 * - `tampon dépassé` grimpe → le délai de restitution est plus court que le
 *   trajet et sa gigue : des notes de partenaires sonnent hors de la grille.
 * - `dérive corrigée` grandit sans cesse → les deux quartz diffèrent, et sa
 *   pente dit de combien ; au-delà de six millisecondes par minute, c'est plus
 *   que l'asservissement ne sait suivre (voir `ClockAligner`).
 * - `sortie audio brute` diffère de `ma sortie audio` → le tampon tenu est en
 *   train de rattraper le relevé, ou l'ignore à raison ; les deux côte à côte
 *   disent lequel (voir `OutputTimestampTimebase.sampleOutputLatency`).
 *
 * Le délai de restitution est lu sur le transport, pas sur l'état React qui
 * l'a demandé : c'est la grandeur qui décide de tout ce qui sonne, et le
 * panneau doit montrer ce qui est en vigueur, pas ce qui a été voulu. Mon
 * délai, lui, est ce que le service m'a donné — le délai commun publié en jam,
 * la réponse à ma dernière annonce sinon : les deux côte à côte disent si
 * l'un a bien suivi l'autre.
 *
 * # La lecture périodique
 *
 * La position avance en continu et n'est pas observable : on l'échantillonne.
 * Un quart de seconde suffit pour un affichage, et l'intervalle ne tourne que
 * pendant qu'on regarde ce panneau.
 */
export function LiveDiagnostics({ active }: { active: boolean }) {
  const live = useLiveSession();
  const { studio } = useStudio();

  /**
   * Le relevé ne tourne que si le panneau est ouvert **et** qu'une séance est
   * en cours. Le panneau reste monté à largeur nulle quand il est fermé, et le
   * contexte live existe sous son fournisseur même hors séance : sans ces deux
   * conditions, un `setState` à 4 Hz re-rendait ce composant sur toutes les
   * pages, en permanence, pour afficher des tirets que personne ne voyait.
   */
  const inSession = live !== null && live.status !== "off";
  const sampling = active && inSession;

  /**
   * Les deux horloges, échantillonnées ensemble.
   *
   * En état et non par `useSyncExternalStore` : son instantané doit être
   * **stable entre deux notifications**, et `elapsedTime()` change à chaque
   * lecture. React comparait, voyait une différence, re-rendait, recomparait —
   * « Maximum update depth exceeded ». Un intervalle qui pose un état est ici
   * la forme simple et correcte.
   *
   * Les deux valeurs viennent du même relevé, sans quoi la position visée
   * serait calculée à un instant différent de la position affichée, et l'écart
   * qu'on cherche à lire serait pollué par celui qu'on introduit.
   */
  const [sample, setSample] = useState<{
    positionMs: number;
    clockMs: number;
    playoutDelayMs: number;
    overruns: number;
    rawOutputMs: number;
  } | null>(null);

  useEffect(() => {
    if (!sampling) {
      return;
    }

    const transport = studio?.getTransport();
    const local = studio?.getLocalTransport();
    const context = studio?.getAudioContext();

    if (transport === undefined || local === undefined || context === undefined) {
      return;
    }

    // Pas de relevé immédiat : ce serait un `setState` synchrone dans un effet.
    // Un quart de seconde de tirets ne gêne personne.
    const handle = window.setInterval(() => {
      setSample({
        positionMs: transport.elapsedTime() * 1000,
        clockMs: transport.clock.currentTime * 1000,
        playoutDelayMs: local.getPlayoutDelay() * 1000,
        overruns: playoutOverrunCount(),
        // Ce que le navigateur annonce à cet instant, avant tout filtre.
        rawOutputMs: outputLatencySeconds(context) * 1000,
      });
    }, 250);

    return () => window.clearInterval(handle);
  }, [sampling, studio]);

  if (live === null) {
    return null;
  }

  const { sessionState, clockOffsetMs, clockDriftMs, delayMs, identity } = live;
  const known = sample !== null && sessionState !== null && clockOffsetMs !== null;

  const targetMs = known ? positionNowMs(sessionState, sample.clockMs, clockOffsetMs) : null;
  const ageMs = known ? sample.clockMs + clockOffsetMs - sessionState.atMs : null;

  return (
    <div className="space-y-0.5 border-t border-border pt-1 text-[12px] text-text-muted">
      <Row label="ma position" value={format(sample?.positionMs ?? null)} />
      <Row label="position visée" value={format(targetMs)} />
      <Row label="position publiée" value={format(sessionState?.positionMs ?? null)} />
      <Row label="âge de l'annonce" value={format(ageMs)} />
      <Row
        label="écart d'horloge"
        value={clockOffsetMs === null ? "—" : `${Math.round(clockOffsetMs)} ms`}
      />
      <Row
        label="dérive corrigée"
        value={clockDriftMs === null ? "—" : `${clockDriftMs.toFixed(1)} ms`}
      />
      <Row
        label="mon délai"
        value={sessionState === null ? "—" : `${Math.round(delayMs)} ms`}
      />
      <Row
        label="délai de restitution"
        value={sample === null ? "—" : `${Math.round(sample.playoutDelayMs)} ms`}
      />
      <Row label="tampon dépassé" value={sample === null ? "—" : `${sample.overruns}`} />
      <Row
        label="sortie audio brute"
        value={sample === null ? "—" : `${sample.rawOutputMs.toFixed(1)} ms`}
      />
      <Row
        label="état"
        value={
          sessionState === null
            ? "aucun"
            : `${sessionState.paused ? "en pause" : "en marche"} · ${regimeLabel(sessionState.jam, sessionState.lead, identity)} · rév. ${sessionState.rev}`
        }
      />
    </div>
  );
}

/**
 * Le régime en un mot, et qui mène : l'identité brute plutôt qu'un nom, parce
 * que ce panneau sert à comparer deux écrans, et qu'une identité se compare.
 */
function regimeLabel(jam: boolean, lead: string, identity: string | null): string {
  if (jam) {
    return "jam";
  }

  if (lead === "") {
    return "sans lead";
  }

  return lead === identity ? "lead : moi" : `lead : ${lead}`;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

/** Une durée en millisecondes, rendue en secondes lisibles. */
function format(ms: number | null): string {
  if (ms === null) {
    return "—";
  }

  return `${(ms / 1000).toFixed(2)} s`;
}
